// headers.interceptor.ts
import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
// import { SessionService } from './session.service';
import { environment } from '../../../environments/environment';
import { CommonCommunicationService } from '../services/commonCommunication.service';

@Injectable()
export class HeadersInterceptor implements HttpInterceptor {
  constructor(private ccs: CommonCommunicationService) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Skip third-party/external URLs
    const skipDomains: string[] = (environment as any).skipAuthDomains || [];
    if (skipDomains.some((domain: string) => req.url.includes(domain))) {
      return next.handle(req);
    }

    let user = this.ccs.userData;

    const isFormData = req.body instanceof FormData;

    let headers: any = {
      // --- Auth ---
      // Authorization: `Bearer ${this.ccs.getAuthToken()}`,
      'X-User-Access-Token': `${this.ccs.getAuthToken()}`,

      // --- Content ---
      Accept: 'application/json',
      'Accept-Language': navigator.language || 'en-US',

      // --- Security ---
      'X-CSRF-Token': this.getCsrfToken(),

      // --- Device & Browser ---
      'X-Device-Type': this.getDeviceType(),
      'X-Device-OS': this.getOS(),
      'X-Browser': this.getBrowser(),
      'X-Browser-Version': this.getBrowserVersion(),
      'X-Screen-Resolution': `${screen.width}x${screen.height}`,
      'X-Viewport': `${window.innerWidth}x${window.innerHeight}`,
      'X-Platform': navigator.platform,

      // --- Date & Time ---
      'X-Request-Timestamp': new Date().toISOString(),
      'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
      'X-Timezone-Offset': new Date().getTimezoneOffset().toString(),

      // --- Tracing ---
      'X-Correlation-ID': uuidv4(),
      // 'X-Session-ID': this.sessionService.getSessionId(),
      'X-Request-ID': uuidv4(),

      // --- App Info ---
      'X-App-Version': environment.appVersion,
      'X-App-Name': environment.appName,
      'X-App-Environment': environment.name,
      'X-Build-Number': environment.buildNumber,
      'API-Version': 'v1',

      // --- User Context ---
      // 'X-User-ID': this.authService.getUserId(),
      // 'X-Tenant-ID': this.authService.getTenantId(),
      'X-User-ID': user?._id ?? '',
      'X-Shop-ID': user?.shop_id?.[0] ?? '',
      // --- Network ---
      'X-Network-Type': this.getNetworkType(),

      // --- Cache (for sensitive endpoints) ---
      ...(this.isSensitiveEndpoint(req.url)
        ? {
            'Cache-Control': 'no-store',
            Pragma: 'no-cache'
          }
        : {})
    };

    if (!isFormData) {
      headers['Content-Type'] = 'application/json';
    }

    const cloned = req.clone({
      setHeaders: headers
    });

    return next.handle(cloned);
  }

  // ---- Utility Methods ----

  private getCsrfToken(): string {
    return document.cookie.match(/XSRF-TOKEN=([^;]+)/)?.[1] || '';
  }

  private getDeviceType(): string {
    const ua = navigator.userAgent;
    if (/Mobi|Android/i.test(ua)) return 'mobile';
    if (/Tablet|iPad/i.test(ua)) return 'tablet';
    return 'desktop';
  }

  private getOS(): string {
    const ua = navigator.userAgent;
    if (/Windows/i.test(ua)) return 'Windows';
    if (/Mac OS X/i.test(ua)) return 'macOS';
    if (/Android/i.test(ua)) return 'Android';
    if (/iPhone|iPad/i.test(ua)) return 'iOS';
    if (/Linux/i.test(ua)) return 'Linux';
    return 'Unknown';
  }

  private getBrowser(): string {
    const ua = navigator.userAgent;
    if (/Edg/i.test(ua)) return 'Edge';
    if (/Chrome/i.test(ua)) return 'Chrome';
    if (/Firefox/i.test(ua)) return 'Firefox';
    if (/Safari/i.test(ua)) return 'Safari';
    if (/Opera|OPR/i.test(ua)) return 'Opera';
    return 'Unknown';
  }

  private getBrowserVersion(): string {
    const ua = navigator.userAgent;
    const match = ua.match(/(Chrome|Firefox|Safari|Edg|Opera|OPR)\/([\d.]+)/i);
    return match ? match[2] : 'Unknown';
  }

  private getNetworkType(): string {
    const conn = (navigator as any).connection;
    return conn?.effectiveType || 'unknown'; // 4g | 3g | 2g | slow-2g
  }

  private isSensitiveEndpoint(url: string): boolean {
    const sensitivePatterns = ['/auth', '/user', '/payment', '/profile'];
    return sensitivePatterns.some((pattern) => url.includes(pattern));
  }
}
